import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@repo/ui/components/chart";
import { Checkbox } from "@repo/ui/components/checkbox";
import { Skeleton } from "@repo/ui/components/skeleton";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import {
  CalendarDaysIcon,
  Clock3Icon,
  MedalIcon,
  TriangleAlertIcon,
  UsersIcon,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/_authed/analytics/work-time")({
  head: () => ({ meta: [{ title: "Work Time" }] }),
  component: RouteComponent,
});

const HOUR_IN_MS = 60 * 60 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const LONG_SESSION_ALERT_HOURS = 12;
const LOW_ACTIVITY_RATIO = 0.45;
const MAX_LOW_ACTIVITY_ALERTS = 6;
const PRESET_RANGE_DAYS = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
} as const;
const CHART_COLORS = [
  "#0f766e",
  "#2563eb",
  "#ea580c",
  "#16a34a",
  "#dc2626",
  "#0891b2",
  "#d97706",
  "#334155",
  "#7c3aed",
  "#b45309",
  "#be123c",
  "#047857",
];

const totalsPieConfig: ChartConfig = {
  totalHours: {
    label: "Total Hours",
    color: "#0f766e",
  },
};

const sessionBehaviorConfig: ChartConfig = {
  averageSessionHours: {
    label: "Average Session",
    color: "#2563eb",
  },
  longestSessionHours: {
    label: "Longest Session",
    color: "#ea580c",
  },
};

const dayFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
});

const longDayFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const hoursFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const msToHours = (value: number) => Number((value / HOUR_IN_MS).toFixed(2));
const formatHours = (value: number) => `${hoursFormatter.format(value)}h`;
const parseDay = (value: string) => new Date(`${value}T00:00:00.000Z`);

type AnalyticsData = NonNullable<
  (typeof api.timeTrackingSimple.getWorkTimeAnalytics)["_returnType"]
>;

type RangePreset = "7d" | "30d" | "90d" | "currentWeek" | "previousWeek" | "ytd" | "custom";
type CalendarRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

const getUtcDayStart = (timestamp: number) => {
  const date = new Date(timestamp);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const getUtcWeekStart = (timestamp: number) => {
  const utcDay = new Date(timestamp).getUTCDay();
  const daysFromMonday = (utcDay + 6) % 7;
  return getUtcDayStart(timestamp) - daysFromMonday * DAY_IN_MS;
};

const filterAnalyticsToRange = (analytics: AnalyticsData, rangeStart: number, rangeEnd: number) => {
  const normalizedStart = getUtcDayStart(rangeStart);
  const normalizedEnd = Math.max(normalizedStart, rangeEnd);
  const normalizedEndDayStart = getUtcDayStart(normalizedEnd);

  const timelineInRange = analytics.timeline.filter((day) => {
    const dayStart = parseDay(day.date).getTime();
    return dayStart >= normalizedStart && dayStart <= normalizedEndDayStart;
  });

  const photographers = [...analytics.photographers]
    .map((photographer) => {
      const dailyMs = timelineInRange.map((day) => day.dailyByUser[photographer.userId] ?? 0);
      const totalMs = dailyMs.reduce((total, value) => total + value, 0);
      const sessionCount = dailyMs.filter((value) => value > 0).length;

      return {
        userId: photographer.userId,
        name: photographer.name,
        image: photographer.image,
        totalMs,
        sessionCount,
        averageSessionMs: sessionCount === 0 ? 0 : totalMs / sessionCount,
        longestSessionMs: Math.max(...dailyMs, 0),
      };
    })
    .toSorted((a, b) => b.totalMs - a.totalMs || a.name.localeCompare(b.name));

  const runningTotals = Object.fromEntries(
    photographers.map((photographer) => [photographer.userId, 0]),
  ) as Record<string, number>;

  const timeline = timelineInRange.map((day) => {
    const dailyByUser: Record<string, number> = {};
    const cumulativeByUser: Record<string, number> = {};
    let teamDailyMs = 0;
    let teamCumulativeMs = 0;

    for (const photographer of photographers) {
      const dailyMs = day.dailyByUser[photographer.userId] ?? 0;
      const cumulativeMs = (runningTotals[photographer.userId] ?? 0) + dailyMs;

      runningTotals[photographer.userId] = cumulativeMs;
      dailyByUser[photographer.userId] = dailyMs;
      cumulativeByUser[photographer.userId] = cumulativeMs;
      teamDailyMs += dailyMs;
      teamCumulativeMs += cumulativeMs;
    }

    return {
      date: day.date,
      dailyByUser,
      cumulativeByUser,
      teamDailyMs,
      teamCumulativeMs,
    };
  });

  const totalTeamMs = photographers.reduce(
    (total, photographer) => total + photographer.totalMs,
    0,
  );
  const totalSessions = photographers.reduce(
    (total, photographer) => total + photographer.sessionCount,
    0,
  );

  return {
    rangeStart: normalizedStart,
    rangeEnd: normalizedEnd,
    photographers,
    timeline,
    totalTeamMs,
    totalSessions,
  } satisfies AnalyticsData;
};

const buildMockAnalytics = () => {
  const dayCount = 180;
  const rangeStart = getUtcDayStart(Date.now() - (dayCount - 1) * DAY_IN_MS);
  const photographerSeeds = [
    {
      userId: "mock-pawel",
      name: "Pawel",
      image: null,
      dailyHours: [8, 6, 9, 7, 6, 8, 7, 9, 6, 7, 8, 6, 9, 7],
    },
    {
      userId: "mock-michal",
      name: "Michal",
      image: null,
      dailyHours: [10, 4, 12, 3, 11, 2, 10, 4, 12, 3, 11, 2, 10, 4],
    },
    {
      userId: "mock-ania",
      name: "Ania",
      image: null,
      dailyHours: [6, 7, 5, 6, 5, 6, 7, 8, 6, 7, 5, 6, 7, 8],
    },
    {
      userId: "mock-kasia",
      name: "Kasia",
      image: null,
      dailyHours: [11, 10, 12, 9, 11, 10, 12, 9, 11, 10, 12, 9, 11, 10],
    },
    {
      userId: "mock-tomek",
      name: "Tomek",
      image: null,
      dailyHours: [3, 0, 4, 0, 3, 0, 5, 0, 4, 0, 3, 0, 5, 0],
    },
    {
      userId: "mock-ola",
      name: "Ola",
      image: null,
      dailyHours: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
    },
    {
      userId: "mock-damian",
      name: "Damian",
      image: null,
      dailyHours: [12, 11, 10, 12, 11, 10, 12, 11, 10, 12, 11, 10, 12, 11],
    },
    {
      userId: "mock-kamil",
      name: "Kamil",
      image: null,
      dailyHours: [2, 2, 14, 2, 2, 14, 2, 2, 14, 2, 2, 14, 2, 2],
    },
    {
      userId: "mock-marta",
      name: "Marta",
      image: null,
      dailyHours: [0, 0, 0, 8, 9, 10, 0, 0, 0, 8, 9, 10, 0, 0],
    },
    {
      userId: "mock-piotr",
      name: "Piotr",
      image: null,
      dailyHours: [7, 8, 6, 7, 8, 6, 7, 8, 6, 7, 8, 6, 7, 8],
    },
    {
      userId: "mock-natalia",
      name: "Natalia",
      image: null,
      dailyHours: [9, 9, 9, 9, 9, 9, 5, 5, 5, 5, 5, 5, 3, 3],
    },
    {
      userId: "mock-bartek",
      name: "Bartek",
      image: null,
      dailyHours: [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2],
    },
  ];

  const hoursByUser = Object.fromEntries(
    photographerSeeds.map((seed) => [
      seed.userId,
      Array.from({ length: dayCount }, (_unused, dayIndex) => {
        return seed.dailyHours[dayIndex % seed.dailyHours.length] ?? 0;
      }),
    ]),
  ) as Record<string, number[]>;

  const photographers = photographerSeeds.map((seed) => {
    const allHours = hoursByUser[seed.userId] ?? [];
    const sessionCount = allHours.filter((hours) => hours > 0).length;
    const totalMs = allHours.reduce((total, hours) => total + hours * HOUR_IN_MS, 0);
    const longestSessionMs = Math.max(...allHours, 0) * HOUR_IN_MS;

    return {
      userId: seed.userId,
      name: seed.name,
      image: seed.image,
      totalMs,
      sessionCount,
      averageSessionMs: sessionCount === 0 ? 0 : totalMs / sessionCount,
      longestSessionMs,
    };
  });
  photographers.sort((a, b) => b.totalMs - a.totalMs || a.name.localeCompare(b.name));

  const runningTotals = Object.fromEntries(
    photographers.map((photographer) => [photographer.userId, 0]),
  ) as Record<string, number>;

  const timeline = Array.from({ length: dayCount }, (_unused, dayIndex) => {
    const date = new Date(rangeStart + dayIndex * DAY_IN_MS).toISOString().slice(0, 10);
    const dailyByUser: Record<string, number> = {};
    const cumulativeByUser: Record<string, number> = {};
    let teamDailyMs = 0;
    let teamCumulativeMs = 0;

    for (const photographer of photographers) {
      const dailyHours = hoursByUser[photographer.userId]?.[dayIndex] ?? 0;
      const dailyMs = dailyHours * HOUR_IN_MS;
      const cumulativeMs = (runningTotals[photographer.userId] ?? 0) + dailyMs;

      runningTotals[photographer.userId] = cumulativeMs;
      dailyByUser[photographer.userId] = dailyMs;
      cumulativeByUser[photographer.userId] = cumulativeMs;
      teamDailyMs += dailyMs;
      teamCumulativeMs += cumulativeMs;
    }

    return {
      date,
      dailyByUser,
      cumulativeByUser,
      teamDailyMs,
      teamCumulativeMs,
    };
  });

  const totalTeamMs = photographers.reduce(
    (total, photographer) => total + photographer.totalMs,
    0,
  );
  const totalSessions = photographers.reduce(
    (total, photographer) => total + photographer.sessionCount,
    0,
  );

  return {
    rangeStart,
    rangeEnd: rangeStart + Math.max(dayCount - 1, 0) * DAY_IN_MS + (DAY_IN_MS - 1),
    photographers,
    timeline,
    totalTeamMs,
    totalSessions,
  } satisfies AnalyticsData;
};

function RouteComponent() {
  const [useMockData] = React.useState(true);
  const [rangePreset] = React.useState<RangePreset>("7d");
  const [calendarRange] = React.useState<CalendarRange>(() => {
    const now = Date.now();

    return {
      from: new Date(getUtcDayStart(now - 29 * DAY_IN_MS)),
      to: new Date(now),
    };
  });

  const selectedRange = React.useMemo(() => {
    const now = Date.now();

    if (rangePreset === "ytd") {
      return {
        rangeStart: Date.UTC(new Date(now).getUTCFullYear(), 0, 1),
        rangeEnd: now,
      };
    }

    if (rangePreset === "custom") {
      const from = calendarRange.from;
      const to = calendarRange.to ?? calendarRange.from;

      if (!from || !to) {
        return {
          rangeStart: Date.UTC(new Date(now).getUTCFullYear(), 0, 1),
          rangeEnd: now,
        };
      }

      const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
      const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) + DAY_IN_MS - 1;
      const clampedEnd = Math.min(toUtc, now);

      return {
        rangeStart: Math.min(fromUtc, clampedEnd),
        rangeEnd: clampedEnd,
      };
    }

    if (rangePreset === "currentWeek") {
      return {
        rangeStart: getUtcWeekStart(now),
        rangeEnd: now,
      };
    }

    if (rangePreset === "previousWeek") {
      const currentWeekStart = getUtcWeekStart(now);

      return {
        rangeStart: currentWeekStart - 7 * DAY_IN_MS,
        rangeEnd: currentWeekStart - 1,
      };
    }

    const dayCount = PRESET_RANGE_DAYS[rangePreset];

    return {
      rangeStart: getUtcDayStart(now - (dayCount - 1) * DAY_IN_MS),
      rangeEnd: now,
    };
  }, [calendarRange.from, calendarRange.to, rangePreset]);

  const mockAnalytics = React.useMemo(buildMockAnalytics, []);
  const rangeFilteredMockAnalytics = React.useMemo(
    () => filterAnalyticsToRange(mockAnalytics, selectedRange.rangeStart, selectedRange.rangeEnd),
    [mockAnalytics, selectedRange.rangeEnd, selectedRange.rangeStart],
  );
  const realAnalytics = useQuery(
    api.timeTrackingSimple.getWorkTimeAnalytics,
    useMockData ? "skip" : selectedRange,
  );
  const analytics = useMockData ? rangeFilteredMockAnalytics : realAnalytics;

  const photographers = React.useMemo(
    () =>
      (analytics?.photographers ?? []).map((photographer, index) => ({
        userId: photographer.userId,
        name: photographer.name,
        image: photographer.image,
        totalMs: photographer.totalMs,
        sessionCount: photographer.sessionCount,
        averageSessionMs: photographer.averageSessionMs,
        longestSessionMs: photographer.longestSessionMs,
        seriesKey: `photographer_${index + 1}`,
        color: CHART_COLORS[index % CHART_COLORS.length],
      })),
    [analytics],
  );

  const [visiblePhotographerIds, setVisiblePhotographerIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    setVisiblePhotographerIds((currentIds) => {
      const availableIds = new Set(photographers.map((photographer) => photographer.userId));
      const nextIds = currentIds.filter((id) => availableIds.has(id));

      if (nextIds.length > 0) {
        return nextIds;
      }

      return photographers.map((photographer) => photographer.userId);
    });
  }, [photographers]);

  const visiblePhotographerIdSet = React.useMemo(
    () => new Set(visiblePhotographerIds),
    [visiblePhotographerIds],
  );

  const visiblePhotographers = React.useMemo(
    () => photographers.filter((photographer) => visiblePhotographerIdSet.has(photographer.userId)),
    [photographers, visiblePhotographerIdSet],
  );

  const togglePhotographer = React.useCallback(
    (photographerId: string, checked: boolean) => {
      setVisiblePhotographerIds((currentIds) => {
        const nextIdSet = new Set(currentIds);

        if (checked) {
          nextIdSet.add(photographerId);
        } else {
          nextIdSet.delete(photographerId);
        }

        return photographers
          .map((photographer) => photographer.userId)
          .filter((id) => nextIdSet.has(id));
      });
    },
    [photographers],
  );

  const showAllPhotographers = React.useCallback(() => {
    setVisiblePhotographerIds(photographers.map((photographer) => photographer.userId));
  }, [photographers]);

  const hideAllPhotographers = React.useCallback(() => {
    setVisiblePhotographerIds([]);
  }, []);

  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};

    for (const photographer of photographers) {
      config[photographer.seriesKey] = {
        label: photographer.name,
        color: photographer.color,
      };
    }

    return config;
  }, [photographers]);

  const dailyWorkloadChartData = React.useMemo(() => {
    if (!analytics) {
      return [];
    }

    return analytics.timeline.map((day) => {
      const parsedDate = parseDay(day.date);
      const row: Record<string, number | string> = {
        date: day.date,
        label: dayFormatter.format(parsedDate),
        longLabel: longDayFormatter.format(parsedDate),
      };

      for (const photographer of photographers) {
        row[photographer.seriesKey] = msToHours(day.dailyByUser[photographer.userId] ?? 0);
      }

      return row;
    });
  }, [analytics, photographers]);

  const totalByPhotographerData = React.useMemo(
    () =>
      photographers.map((photographer) => ({
        userId: photographer.userId,
        photographer: photographer.name,
        totalHours: msToHours(photographer.totalMs),
        fill: photographer.color,
      })),
    [photographers],
  );

  const hasPieData = totalByPhotographerData.some((entry) => entry.totalHours > 0);

  const sessionBehaviorData = React.useMemo(
    () =>
      photographers.slice(0, 10).map((photographer) => ({
        userId: photographer.userId,
        photographer: photographer.name,
        averageSessionHours: msToHours(photographer.averageSessionMs),
        longestSessionHours: msToHours(photographer.longestSessionMs),
      })),
    [photographers],
  );

  const hasSessionBehaviorData = sessionBehaviorData.some(
    (entry) => entry.averageSessionHours > 0 || entry.longestSessionHours > 0,
  );

  const longSessionAlerts = React.useMemo(
    () =>
      photographers
        .map((photographer) => ({
          userId: photographer.userId,
          name: photographer.name,
          longestSessionHours: msToHours(photographer.longestSessionMs),
        }))
        .filter((photographer) => photographer.longestSessionHours > LONG_SESSION_ALERT_HOURS)
        .sort((a, b) => b.longestSessionHours - a.longestSessionHours),
    [photographers],
  );

  const lowActivityThresholdHours = React.useMemo(() => {
    if (!analytics) {
      return 0;
    }

    const activeDayHours = analytics.timeline
      .map((day) => msToHours(day.teamDailyMs))
      .filter((hours) => hours > 0);

    if (activeDayHours.length === 0) {
      return 0;
    }

    const averageActiveDayHours =
      activeDayHours.reduce((total, hours) => total + hours, 0) / activeDayHours.length;

    return Number(Math.max(1, averageActiveDayHours * LOW_ACTIVITY_RATIO).toFixed(2));
  }, [analytics]);

  const lowActivityDayAlerts = React.useMemo(() => {
    if (!analytics || lowActivityThresholdHours === 0) {
      return [];
    }

    const lowActivityDays = analytics.timeline
      .map((day) => {
        const teamHours = msToHours(day.teamDailyMs);

        return {
          date: day.date,
          label: longDayFormatter.format(parseDay(day.date)),
          teamHours,
        };
      })
      .filter((day) => day.teamHours > 0 && day.teamHours <= lowActivityThresholdHours);

    lowActivityDays.sort((a, b) => a.teamHours - b.teamHours);

    return lowActivityDays.slice(0, MAX_LOW_ACTIVITY_ALERTS);
  }, [analytics, lowActivityThresholdHours]);

  const maxSessionAxisHours = React.useMemo(() => {
    const maxValue = Math.max(
      ...sessionBehaviorData.map((entry) =>
        Math.max(entry.averageSessionHours, entry.longestSessionHours),
      ),
      0,
    );

    return Math.max(4, Math.ceil(maxValue / 2) * 2 + 2);
  }, [sessionBehaviorData]);

  const sessionAxisTicks = React.useMemo(
    () =>
      Array.from(
        {
          length: Math.floor(maxSessionAxisHours / 2) + 1,
        },
        (_unused, index) => index * 2,
      ),
    [maxSessionAxisHours],
  );

  if (!analytics) {
    return <AnalyticsLoadingState />;
  }

  const totalTeamHours = msToHours(analytics.totalTeamMs);
  const activeDays = analytics.timeline.filter((day) => day.teamDailyMs > 0).length;
  const averageHoursPerActiveDay = activeDays === 0 ? 0 : totalTeamHours / activeDays;
  const averageSessionHours =
    analytics.totalSessions === 0 ? 0 : totalTeamHours / analytics.totalSessions;
  const topPhotographer = photographers[0];

  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Work Time Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Daily workload analytics for the selected date range.
          </p>
        </div>
        {/* <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5">
            <span className="text-xs text-muted-foreground">Mock Data</span>
            <Switch
              checked={useMockData}
              onCheckedChange={setUseMockData}
              aria-label="Toggle mock analytics data"
            />
          </div>
          <Badge variant={useMockData ? "secondary" : "outline"}>
            {useMockData ? "Mock source" : "Convex source"}
          </Badge>
          <Badge variant="outline">
            {rangeFormatter.format(new Date(analytics.rangeStart))} -{" "}
            {rangeFormatter.format(new Date(analytics.rangeEnd))}
          </Badge>
          <Badge variant="secondary">{photographers.length} photographers</Badge>
        </div> */}
      </div>

      {/* <Card size="sm">
        <CardContent className="grid gap-3 pt-5">
          <Popover>
            <PopoverTrigger
              render={
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal md:w-85"
                >
                  <CalendarDaysIcon className="mr-2 size-4" />
                  {customRangeLabel}
                </Button>
              }
            />
            <PopoverContent className="w-auto p-0" align="start">
              <div className="flex flex-col md:flex-row">
                <div className="flex min-w-45 flex-col gap-1 border-b p-2 md:border-r md:border-b-0">
                  {(["currentWeek", "previousWeek", "7d", "30d", "90d", "ytd"] as const).map(
                    (preset) => (
                      <Button
                        key={preset}
                        size="sm"
                        variant={rangePreset === preset ? "secondary" : "ghost"}
                        className="justify-start"
                        onClick={() => applyRangePreset(preset)}
                      >
                        {RANGE_PRESET_LABELS[preset]}
                      </Button>
                    ),
                  )}
                </div>

                <Calendar
                  mode="range"
                  selected={calendarRange}
                  onSelect={(nextRange) => {
                    if (!nextRange?.from) {
                      return;
                    }

                    setRangePreset("custom");
                    setCalendarRange(nextRange);
                  }}
                  numberOfMonths={2}
                  initialFocus
                />
              </div>
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card> */}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card size="sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardDescription>Total Team Hours</CardDescription>
            <Clock3Icon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{formatHours(totalTeamHours)}</div>
            <p className="text-xs text-muted-foreground">
              Across {analytics.totalSessions} sessions.
            </p>
          </CardContent>
        </Card>

        <Card size="sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardDescription>Active Work Days</CardDescription>
            <CalendarDaysIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{activeDays}</div>
            <p className="text-xs text-muted-foreground">
              Days with at least one tracked photographer session.
            </p>
          </CardContent>
        </Card>

        <Card size="sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardDescription>Average / Active Day</CardDescription>
            <UsersIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {formatHours(averageHoursPerActiveDay)}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatHours(averageSessionHours)} average session length.
            </p>
          </CardContent>
        </Card>

        <Card size="sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardDescription>Current Leader</CardDescription>
            <MedalIcon className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="truncate text-xl font-semibold">{topPhotographer?.name ?? "-"}</div>
            <p className="text-xs text-muted-foreground">
              {topPhotographer ? formatHours(msToHours(topPhotographer.totalMs)) : "0.0h"} total.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TriangleAlertIcon className="size-4 text-amber-500" />
            Outlier Alerts
          </CardTitle>
          <CardDescription>
            Auto-flagged unusually long sessions and unusually low activity team days.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Long Sessions</p>
              <Badge variant={longSessionAlerts.length > 0 ? "destructive" : "secondary"}>
                {longSessionAlerts.length} flagged
              </Badge>
            </div>

            {longSessionAlerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No sessions above {LONG_SESSION_ALERT_HOURS}h in this period.
              </p>
            ) : (
              <div className="space-y-2">
                {longSessionAlerts.map((alert) => (
                  <div
                    key={alert.userId}
                    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  >
                    <p className="truncate text-sm font-medium">{alert.name}</p>
                    <Badge variant="destructive">{formatHours(alert.longestSessionHours)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">Low Activity Days</p>
              <Badge variant={lowActivityDayAlerts.length > 0 ? "outline" : "secondary"}>
                {`<= ${formatHours(lowActivityThresholdHours)}`}
              </Badge>
            </div>

            {lowActivityDayAlerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No low-activity days flagged in the selected period.
              </p>
            ) : (
              <div className="space-y-2">
                {lowActivityDayAlerts.map((alert) => (
                  <div
                    key={alert.date}
                    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  >
                    <p className="truncate text-sm font-medium">{alert.label}</p>
                    <Badge variant="outline">{formatHours(alert.teamHours)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {photographers.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No tracked work yet</CardTitle>
            <CardDescription>
              Start a few sessions in Time Tracker and this dashboard will populate automatically.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Daily Workload Split by Photographer</CardTitle>
              <CardDescription>
                Grouped daily bars to compare each photographer's workload side by side.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={showAllPhotographers}
                  disabled={visiblePhotographers.length === photographers.length}
                >
                  Show all
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={hideAllPhotographers}
                  disabled={visiblePhotographers.length === 0}
                >
                  Hide all
                </Button>
                <Badge variant="outline">
                  {visiblePhotographers.length}/{photographers.length} visible
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2">
                {photographers.map((photographer) => {
                  const checked = visiblePhotographerIdSet.has(photographer.userId);

                  return (
                    <label
                      key={photographer.userId}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1 text-xs"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) =>
                          togglePhotographer(photographer.userId, !!value)
                        }
                        aria-label={`Toggle ${photographer.name} series`}
                      />
                      <span
                        className="size-2 rounded-full"
                        style={{ backgroundColor: photographer.color }}
                      />
                      <span className={checked ? "text-foreground" : "text-muted-foreground"}>
                        {photographer.name}
                      </span>
                    </label>
                  );
                })}
              </div>

              {visiblePhotographers.length === 0 ? (
                <div className="flex h-87.5 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                  Select at least one photographer to render workload bars.
                </div>
              ) : (
                <ChartContainer config={chartConfig} className="h-87.5 w-full">
                  <BarChart
                    accessibilityLayer
                    data={dailyWorkloadChartData}
                    margin={{ left: 8, right: 8 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      minTickGap={24}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={52}
                      tickFormatter={(value: number) => `${value}h`}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          indicator="dashed"
                          labelFormatter={(_, payload) => {
                            const label = payload?.[0]?.payload?.longLabel;
                            return typeof label === "string" ? label : "";
                          }}
                        />
                      }
                      cursor={false}
                    />
                    <ChartLegend
                      content={<ChartLegendContent className="flex-wrap justify-start" />}
                    />
                    {visiblePhotographers.map((photographer) => (
                      <Bar
                        key={photographer.userId}
                        dataKey={photographer.seriesKey}
                        fill={`var(--color-${photographer.seriesKey})`}
                        radius={4}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 xl:grid-cols-5">
            <Card className="xl:col-span-3">
              <CardHeader>
                <CardTitle>Session Depth by Photographer</CardTitle>
                <CardDescription>
                  Compare average session length against each photographer's longest session.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hasSessionBehaviorData ? (
                  <ChartContainer config={sessionBehaviorConfig} className="h-105 w-full">
                    <BarChart data={sessionBehaviorData} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid vertical={false} />
                      <XAxis
                        dataKey="photographer"
                        tickLine={false}
                        axisLine={false}
                        minTickGap={24}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={52}
                        domain={[0, maxSessionAxisHours]}
                        ticks={sessionAxisTicks}
                        interval={0}
                        tickFormatter={(value: number) => `${value}h`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            indicator="line"
                            labelFormatter={(_, payload) => {
                              const label = payload?.[0]?.payload?.photographer;
                              return typeof label === "string" ? label : "";
                            }}
                          />
                        }
                      />
                      <ChartLegend
                        content={<ChartLegendContent className="flex-wrap justify-start" />}
                      />
                      <Bar
                        dataKey="averageSessionHours"
                        fill="var(--color-averageSessionHours)"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="longestSessionHours"
                        fill="var(--color-longestSessionHours)"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="flex h-105 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                    No session data yet.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle>Total Share</CardTitle>
                <CardDescription>
                  Pie chart of total tracked hours split across all photographers.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4">
                {hasPieData ? (
                  <ChartContainer config={totalsPieConfig} className="h-62.5 w-full">
                    <PieChart>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            hideIndicator
                            formatter={(value, name) => (
                              <div className="flex w-full items-center justify-between gap-2">
                                <span className="text-muted-foreground">{name}</span>
                                <span className="font-mono font-medium tabular-nums">
                                  {formatHours(Number(value))}
                                </span>
                              </div>
                            )}
                          />
                        }
                      />
                      <Pie
                        data={totalByPhotographerData}
                        dataKey="totalHours"
                        nameKey="photographer"
                        innerRadius={54}
                        outerRadius={92}
                        paddingAngle={1}
                        strokeWidth={2}
                      >
                        {totalByPhotographerData.map((entry) => (
                          <Cell key={entry.userId} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                ) : (
                  <div className="flex h-62.5 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                    No tracked totals yet.
                  </div>
                )}

                <div className="space-y-3">
                  {photographers.slice(0, 4).map((photographer, index) => {
                    const share =
                      analytics.totalTeamMs === 0
                        ? 0
                        : Math.round((photographer.totalMs / analytics.totalTeamMs) * 100);

                    return (
                      <div key={photographer.userId} className="space-y-1">
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <p className="truncate font-medium">
                            #{index + 1} {photographer.name}
                          </p>
                          <p className="shrink-0 text-muted-foreground tabular-nums">{share}%</p>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${share}%`, backgroundColor: photographer.color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function AnalyticsLoadingState() {
  return (
    <div className="grid gap-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} size="sm">
            <CardHeader className="space-y-2 pb-1">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-6 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-2">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-87.5 w-full" />
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-5">
        <Card className="xl:col-span-3">
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-72" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-105 w-full" />
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader className="space-y-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-80 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
